import { DashboardApi20240730 } from '@sequencehq/api/dashboard/v20240730'
import { add, getDate, isAfter, parse } from '@sequencehq/utils/dates'
import { atom } from 'jotai'
import { APIPhase, scheduleDataAtom, ProcessedPhase } from './scheduleData.atom'
import { parseBillingSchedulePhaseDuration } from 'lib/schedules/parseBillingSchedulePhaseDuration'
import { API_DATE_FORMAT } from 'BillingScheduleHome/billingScheduleHome.constants'

type APIPrice = Exclude<DashboardApi20240730.GetPrice.Response['data'], null>

export const sortArrayByStartDate = <
  Model extends { startDate: Date | null | undefined }
>(
  phases: Model[]
): Model[] => {
  return phases.sort(({ startDate: startDateA }, { startDate: startDateB }) => {
    if (!startDateA) {
      return 1
    }
    if (!startDateB) {
      return -1
    }
    return isAfter(startDateA, startDateB) ? 1 : -1
  })
}

export const calculateRecurrenceDatesForPhases = (args: {
  phases: Array<Omit<ProcessedPhase, 'recurrenceDayOfMonthForPhase'>>
  recurrenceDayOfMonth: number
}): Array<ProcessedPhase> => {
  return args.phases.reduce(
    (acc, phase) => {
      const previousPhase = acc[acc.length - 1]
      if (!previousPhase) {
        return [
          {
            ...phase,
            recurrenceDayOfMonthForPhase: args.recurrenceDayOfMonth
          }
        ]
      }

      if (phase.recurrencePreference === 'CONTINUE_FROM_PREVIOUS_PHASE') {
        return [
          ...acc,
          {
            ...phase,
            recurrenceDayOfMonthForPhase:
              previousPhase.recurrenceDayOfMonthForPhase
          }
        ]
      }

      return [
        ...acc,
        {
          ...phase,
          recurrenceDayOfMonthForPhase: getDate(phase.startDate)
        }
      ]
    },
    [] as Array<ProcessedPhase>
  )
}

const getPhaseDatesAndDuration = (
  phase: APIPhase
): {
  start: Date
  end: Date
  openEnded: boolean
} => {
  const startDate = parse(phase.startDate, API_DATE_FORMAT, new Date())
  const endDate = phase.endDate
    ? parse(phase.endDate, API_DATE_FORMAT, new Date())
    : undefined
  const duration = parseBillingSchedulePhaseDuration({
    startDate,
    endDate
  })

  /**
   * If the phase duration is open ended or milestone, and therefore not
   * represented as a date, we will flag the phase as open ended, and
   * set the phase to be 1 year from a timeline perspective.
   */

  if (typeof duration === 'string' || !endDate) {
    return {
      start: startDate,
      end: add(startDate, { years: 1, days: -1 }),
      openEnded: true
    }
  }

  return {
    start: startDate,
    end: endDate,
    openEnded: false
  }
}

export const processedPhasesAtom = atom<Array<ProcessedPhase>>(get => {
  const scheduleData = get(scheduleDataAtom)
  if (!scheduleData) {
    return []
  }

  const phasesWithParsedDates: Array<
    Omit<ProcessedPhase, 'recurrenceDayOfMonthForPhase'>
  > = scheduleData.phases.map(phase => {
    const {
      start: startDate,
      end: endDate,
      openEnded
    } = getPhaseDatesAndDuration(phase)

    return {
      id: phase.id,
      startDate,
      /**
       * When a phase is open ended, we will display
       * a year on the timeline in the 'open ended'
       * visualisation style.
       */
      endDate: openEnded
        ? add(phase.startDate, { years: 1, days: -1 })
        : endDate,
      openEnded,
      name: phase.name,
      prices: phase.priceIds
        .map(priceId => scheduleData.prices.find(price => price.id === priceId))
        .filter((price): price is APIPrice => Boolean(price)),
      discounts: phase.discounts,
      minimums: phase.minimums,
      recurrencePreference: phase.recurrencePreference
    }
  })

  const sortedPhases = sortArrayByStartDate<
    Omit<ProcessedPhase, 'recurrenceDayOfMonthForPhase'>
  >(phasesWithParsedDates)

  const phasesWithRecurrenceDates = calculateRecurrenceDatesForPhases({
    recurrenceDayOfMonth: scheduleData.recurrenceDayOfMonth,
    phases: sortedPhases
  })

  const firstOpenEndedPhase = phasesWithRecurrenceDates.findIndex(phase => {
    return phase.openEnded
  })

  return firstOpenEndedPhase === -1
    ? phasesWithRecurrenceDates
    : phasesWithRecurrenceDates.slice(0, firstOpenEndedPhase + 1)
})
